import { useState, useRef } from 'react';
import api from '../../services/api';

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : '';

export function AvatarDisplay({ src, name, size = 36, style = {} }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const imgUrl = src ? (src.startsWith('http') ? src : (api.resolveFileUrl(src) || `${API_BASE}${src}`)) : null;
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div style={{
      width: size, height: size, borderRadius: size > 40 ? 14 : 10,
      background: imgUrl ? 'transparent' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: Math.max(10, size * 0.35),
      fontFamily: "'Plus Jakarta Sans',sans-serif",
      overflow: 'hidden', flexShrink: 0, ...style
    }}>
      {imgUrl && !imgFailed ? (
        <img
          src={imgUrl}
          alt={name || 'avatar'}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImgFailed(true)}
        />
      ) : initials}
    </div>
  );
}

export function AvatarUploadModal({ currentAvatar, name, onClose, onUploaded }) {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError('File must be under 5MB'); return; }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('Only JPG, PNG, WEBP allowed'); return;
    }
    setError('');
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await api.uploadAvatar(fd);
      onUploaded(res.avatar || res.avatarUrl || res.url);
      onClose();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
      backdropFilter: 'blur(4px)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 24, width: 380, maxWidth: '90vw',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Profile Photo</h3>
          <button onClick={onClose} style={{
            border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 6,
            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 14, color: '#64748b'
          }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <AvatarDisplay
            src={preview || currentAvatar}
            name={name}
            size={100}
            style={{ borderRadius: 20, border: '3px solid #e2e8f0' }}
          />

          <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
          <button onClick={() => inputRef.current?.click()} style={{
            padding: '8px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0',
            background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            fontFamily: 'inherit', color: '#475569'
          }}>
            Choose Photo
          </button>

          {error && <div style={{ color: '#ef4444', fontSize: 12, fontWeight: 600 }}>{error}</div>}

          {file && (
            <button onClick={handleUpload} disabled={uploading} style={{
              width: '100%', padding: '10px', borderRadius: 8, border: 'none',
              background: uploading ? '#94a3b8' : 'linear-gradient(135deg,#4338ca,#6366f1)',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit'
            }}>
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AvatarUpload({ currentAvatar, name, size = 36, editable = false, onUploaded }) {
  const [showModal, setShowModal] = useState(false);
  const [avatar, setAvatar] = useState(currentAvatar);

  const handleUploaded = (url) => {
    setAvatar(url);
    onUploaded?.(url);
  };

  return (
    <>
      <div
        style={{ position: 'relative', cursor: editable ? 'pointer' : 'default' }}
        onClick={() => editable && setShowModal(true)}
      >
        <AvatarDisplay src={avatar || currentAvatar} name={name} size={size} />
        {editable && (
          <div style={{
            position: 'absolute', bottom: -2, right: -2,
            width: 18, height: 18, borderRadius: '50%',
            background: '#6366f1', border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
        )}
      </div>
      {showModal && (
        <AvatarUploadModal
          currentAvatar={avatar || currentAvatar}
          name={name}
          onClose={() => setShowModal(false)}
          onUploaded={handleUploaded}
        />
      )}
    </>
  );
}
